import { defineStore } from 'pinia'
import axiosInstance from '@/config/axios'
import axios from 'axios'  // Conserver pour la configuration globale
import router from '@/router'

// Nous n'importons pas les autres stores ici pour éviter les dépendances circulaires

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false
  }),

  actions: {
    async login(email, password) {
      try {
        // Réinitialiser les données des autres stores avant la connexion
        this.resetStores()
        
        const response = await axiosInstance.post('/auth/login/', { email, password })
        
        if (response.data.token && response.data.user) {
          this.setAuth(response.data.user, response.data.token, response.data.refresh)
          return response.data
        } else {
          throw new Error('Réponse invalide du serveur')
        }
      } catch (error) {
        
        throw error
      }
    },

    setAuth(user, accessToken, refreshToken) {
      this.user = user
      this.accessToken = accessToken
      this.refreshToken = refreshToken
      this.isAuthenticated = true

      // Stocker les tokens dans le localStorage
      localStorage.setItem('access_token', accessToken)
      localStorage.setItem('refresh_token', refreshToken)
      localStorage.setItem('user', JSON.stringify(user))
      
      // Configurer axios avec le token
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    },

    logout() {
      // Réinitialiser les données des autres stores
      this.resetStores()
      
      this.user = null
      this.accessToken = null
      this.refreshToken = null
      this.isAuthenticated = false

      // Nettoyer le localStorage
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      
      // Supprimer le header d'autorisation
      delete axios.defaults.headers.common['Authorization']
      
      // Rediriger vers la page de login
      router.push('/login')
    },

    initializeAuth() {
      const accessToken = localStorage.getItem('access_token')
      const refreshToken = localStorage.getItem('refresh_token')
      const user = localStorage.getItem('user')

      if (accessToken && refreshToken && user) {
        try {
          const userData = JSON.parse(user)
          this.setAuth(userData, accessToken, refreshToken)
          // Configurer explicitement les intercepteurs Axios
          this.setupAxiosInterceptors()
          return true
        } catch (error) {
          
          this.logout()
          return false
        }
      }
      // Même si l'utilisateur n'est pas authentifié, configurer les intercepteurs
      // pour gérer correctement les erreurs d'authentification
      this.setupAxiosInterceptors()
      return false
    },

    async checkAuth() {
      if (!this.accessToken) {
        return false
      }

      try {
        const response = await axiosInstance.post('/auth/verify/', null, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        })
        
        if (response.data.status === 'valid' && response.data.user) {
          // Mettre à jour les données utilisateur
          this.user = response.data.user
          return true
        }
        
        return false
      } catch (error) {
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.logout()
        }
        return false
      }
    },

    setupAxiosInterceptors() {
      // Configuration globale d'axios
      axios.defaults.withCredentials = true  // Important pour les cookies CSRF
      
      // Restaurer le header d'autorisation si le token existe
      const token = localStorage.getItem('access_token')
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      }
      
      axios.interceptors.response.use(
        response => response,
        async error => {
          // Gérer les erreurs 401 (Non autorisé) et 403 (Interdit)
          if (error.response?.status === 401 || error.response?.status === 403) {
            try {
              // Tenter de rafraîchir le token
              const refreshToken = localStorage.getItem('refresh_token')
              if (refreshToken) {
                const response = await axiosInstance.post('/auth/token/refresh/', {
                  refresh: refreshToken
                })
                
                if (response.data.access) {
                  // Mettre à jour le token
                  this.accessToken = response.data.access
                  localStorage.setItem('access_token', response.data.access)
                  axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`
                  
                  // Réessayer la requête originale
                  error.config.headers['Authorization'] = `Bearer ${response.data.access}`
                  return axios(error.config)
                }
              }
            } catch (refreshError) {
              if (import.meta.env.DEV) {
                // Gestion de l'échec du rafraîchissement du token
              }
              this.logout()
              return Promise.reject(error)
            }
            
            this.logout()
          }
          
          // Ne pas déconnecter pour les erreurs 404
          if (error.response?.status === 404) {
            return Promise.reject(error)
          }
          
          return Promise.reject(error)
        }
      )
    },

    // Méthode pour réinitialiser tous les stores Pinia
    resetStores() {
      try {
        // 1. Nettoyer les caches locaux
        // Supprimer TOUTES les données du localStorage sauf les tokens d'authentification et les préférences de thème
        const keysToKeep = ['access_token', 'refresh_token', 'user', 'app_theme_preference'];
        
        // Parcourir tous les éléments du localStorage et supprimer ceux qui ne sont pas dans keysToKeep
        Object.keys(localStorage).forEach(key => {
          if (!keysToKeep.includes(key)) {
            localStorage.removeItem(key);
          }
        });
        
        // 2. Réinitialiser explicitement les clés connues
        localStorage.removeItem('selectedPortfolioId');
        
        // 3. Essayer de réinitialiser les stores connus avec gestion d'erreur pour chaque store
        // Liste des stores à réinitialiser
        const storeIds = ['cash', 'portfolio', 'asset', 'transaction', 'dividend'];
        
        // Réinitialiser chaque store individuellement avec gestion d'erreur
        import('pinia').then(({ getActivePinia }) => {
          const pinia = getActivePinia();
          if (pinia) {
            storeIds.forEach(storeId => {
              try {
                // Essayer de récupérer le store par son ID
                const store = pinia._s.get(storeId);
                if (store && typeof store.$reset === 'function') {
                  store.$reset();
                }
              } catch (storeError) {
                if (import.meta.env.DEV) {
                  // Impossible de réinitialiser un store spécifique
                }
              }
            });
          }
        }).catch(err => {
          if (import.meta.env.DEV) {
            // Gestion de l'erreur lors de l'importation de Pinia
          }
        });
        
        // 4. Utiliser l'événement personnalisé pour réinitialiser tous les stores
        // Cela évite les dépendances circulaires
        window.dispatchEvent(new CustomEvent('pinia-reset-stores'));
      } catch (error) {
        if (import.meta.env.DEV) {
          // Gestion de l'erreur lors de la réinitialisation des stores
        }
      }
    }
  }
})

